import { FC, PropsWithChildren, createContext, createRef, useImperativeHandle, useMemo, useState } from 'react';
import { Modals } from '../exports/Enums';
import collect from 'collect.js';
import { PresentModalOptions } from '../exports/Types';
import { defaultSwipeableModalOptions } from '../exports/Constants';
import OrderMethod from '../modals/order-method';
import Location from '../modals/location';
import DateTime from '../modals/date-time';
import DeliveryAddress from '../modals/delivery-address';
import Signup from '../modals/sign-up';
import Login from '../modals/login';
import Profile from '../modals/profile';
import PaymentMethods from '../modals/payment-methods';
import ManageAccount from '../modals/manage-account';
import HelpAndFAQs from '../modals/help-and-faq';
import AddCreditCard from '../modals/add-credit-card';
import MenuItem from '../modals/menu-item';
import OrderTracker from '../modals/order-tracker';
import RestaurantClosedModal from '../modals/restaurant-closed/RestaurantClosedModal';
import LanguageSwitcherModal from '../modals/language-switcher';
import OfferModal from '../modals/offer-modal';
import FreeItemsModal from '../modals/free-items';
import MobileAppSignUpBonus from '../modals/mobile-app-sign-up-bonus';
import useAuth from '../../../hooks/global/useAuth';

export interface IModal {
  isOpen: boolean;
  isBack?: boolean;
  goBack: (props?: any) => void;
  closeModal: () => void;
  openModal: (config: IModalConfig) => void;
}

interface IModalRouter {
  id: Modals;
  component: FC<Partial<IModal>> & any;
  guestOnly?: boolean;
  children?: IModalRouter[];
}

interface IModalConfig {
  id: Modals;
  presentation?: PresentModalOptions;
  props?: any;
  onDidPresent?: () => void;
}

export interface IModalRouterContext extends IModal {
  isOpen: boolean;
  config: IModalConfig | null;
  findModal: (id: Modals) => IModalRouter | undefined;
  dismissModal: () => void;
}

export const ModalRouterContext = createContext<IModalRouterContext>({
  config: null,
  isOpen: false,
  openModal: () => undefined,
  goBack: () => undefined,
  findModal: () => undefined,
  closeModal: () => undefined,
  dismissModal: () => undefined,
});

export const modalRouterRef = createRef<IModalRouterContext>();

const ModalRouter: FC<PropsWithChildren> = ({ children }) => {
  const [config, setConfig] = useState<IModalConfig | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isLoggedIn } = useAuth();

  const stack: IModalRouter[] = [
    {
      id: Modals.Profile,
      component: Profile,
      children: [
        {
          id: Modals.PaymentMethods,
          component: PaymentMethods,
          children: [
            {
              id: Modals.AddCreditCard,
              component: AddCreditCard,
            },
          ],
        },
        {
          id: Modals.ManageAccount,
          component: ManageAccount,
        },
        {
          id: Modals.LanguageSwitcher,
          component: LanguageSwitcherModal,
        },
        {
          id: Modals.HelpAndFAQs,
          component: HelpAndFAQs,
        },
      ],
    },
    {
      id: Modals.OrderMethod,
      component: OrderMethod,
      children: [
        {
          id: Modals.DateTime,
          component: DateTime,
        },
        {
          id: Modals.Location,
          component: Location,
        },
        {
          id: Modals.DeliveryAddress,
          component: DeliveryAddress,
        },
      ],
    },
    {
      id: Modals.Signup,
      component: Signup,
      guestOnly: true,
    },
    {
      id: Modals.Login,
      component: Login,
      guestOnly: true,
    },
    {
      id: Modals.MenuItem,
      component: MenuItem,
    },
    {
      id: Modals.OrderTracker,
      component: OrderTracker,
    },
    {
      id: Modals.RestaurantClosed,
      component: RestaurantClosedModal,
    },
    {
      id: Modals.OfferModal,
      component: OfferModal,
    },
    {
      id: Modals.FreeItemsModal,
      component: FreeItemsModal,
    },
    {
      id: Modals.MobileAppSignupBonus,
      component: MobileAppSignUpBonus,
    },
  ];

  const findModal = (id: Modals, modalStack = stack): IModalRouter | undefined => {
    if (!modalStack.length) {
      return;
    }

    let result: IModalRouter;
    const nextStack: IModalRouter[] = [];

    modalStack.forEach((modal) => {
      if (modal.id === id) {
        result = modal;
        return;
      }

      modal.children?.length && nextStack.push(...modal.children);
    });

    if (result) {
      return result;
    }

    return findModal(id, nextStack);
  };

  const findParentModal = (id: Modals, modalStack = stack): IModalRouter | undefined => {
    let result: IModalRouter | undefined;

    collect(modalStack).each((modal) => {
      if (result) return false;

      if (modal.children?.some((child) => child.id === id)) {
        result = modal;
        return false;
      }

      if (modal.children) {
        const parent = findParentModal(id, modal.children);
        if (parent) {
          result = parent;
          return false;
        }
      }
    });

    return result;
  };

  const openModal = (config: IModalConfig): void => {
    const modal = findModal(config?.id);

    if (!modal) {
      return;
    }

    if (modal.guestOnly && isLoggedIn) {
      return;
    }

    setConfig({
      ...config,
      presentation: config?.presentation === undefined ? defaultSwipeableModalOptions : config?.presentation,
    });
    setIsOpen(true);
  };

  const closeModal = (): void => {
    setIsOpen(false);
  };

  const goBack = (props?: any): void => {
    const parentModal = findParentModal(config?.id);
    if (!parentModal) return;
    setConfig((prevConfig) => ({
      ...prevConfig,
      id: parentModal.id,
      props: { isBack: true, ...(props && props) },
    }));
  };

  const dismissModal = (): void => {
    setConfig(null);
    setIsOpen(false);
  };

  const context = useMemo(
    () => ({ isOpen, config, goBack, openModal, findModal, closeModal, dismissModal }),
    [isOpen, config],
  );

  useImperativeHandle(modalRouterRef, () => context);

  return <ModalRouterContext.Provider value={context}>{children}</ModalRouterContext.Provider>;
};

export default ModalRouter;
